/**
 * Cookiebot consent → ClickDimensions hidden fields (all SurveyJS instances on the page).
 */

const CONSENT_FIELD_MARKETING = 'Cookie Bot Marketing';
const CONSENT_FIELD_STATISTICS = 'Cookie Bot Statistics';

/**
 * DOM subtree root usable with querySelector (ParentNode is not a global constructor).
 *
 * @param {unknown} value
 * @returns {value is Document | DocumentFragment | Element}
 */
function isDomSearchScope(value) {
	return Boolean(
		value &&
			(value === document ||
				value instanceof Document ||
				value instanceof DocumentFragment ||
				(value instanceof Element && typeof value.querySelector === 'function'))
	);
}

/**
 * @param {Document | DocumentFragment | Element} scope
 * @returns {Element|null}
 */
function resolveSurveyRoot(scope) {
	if (!scope || typeof scope.querySelector !== 'function') {
		return null;
	}

	return (
		scope.querySelector('.sd-root-modern') ||
		scope.querySelector('.sd-container-modern') ||
		(scope instanceof Element && scope.matches('.sd-root-modern') ? scope : null) ||
		(scope instanceof Element && scope.matches('.sd-container-modern') ? scope : null)
	);
}

/**
 * Survey model from React fiber on a single survey root element.
 *
 * @param {Element} root
 * @returns {object|null}
 */
function getSurveyModelFromRoot(root) {
	if (!root) {
		return null;
	}

	const fiberKey = Object.keys(root).find(
		(k) => k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
	);
	if (!fiberKey) {
		return null;
	}

	let fiber = root[fiberKey];
	let attempts = 0;

	while (fiber && attempts < 50) {
		const props = fiber.memoizedProps || fiber.pendingProps;
		const model = props?.model || props?.survey;

		if (model && typeof model.setValue === 'function') {
			return model;
		}

		fiber = fiber.return;
		attempts++;
	}

	return null;
}

/**
 * All distinct SurveyJS models in one document or element subtree.
 *
 * @param {ParentNode|Document} scope
 * @returns {object[]}
 */
function getSurveyModelsInScope(scope) {
	/** @type {object[]} */
	const models = [];
	const seen = new WeakSet();

	if (!scope || typeof scope.querySelectorAll !== 'function') {
		return models;
	}

	scope.querySelectorAll('.sd-root-modern').forEach((root) => {
		const model = getSurveyModelFromRoot(root);
		if (model && !seen.has(model)) {
			seen.add(model);
			models.push(model);
		}
	});

	if (!models.length) {
		const fallbackRoot = resolveSurveyRoot(scope);
		const model = getSurveyModelFromRoot(fallbackRoot);
		if (model && !seen.has(model)) {
			models.push(model);
		}
	}

	return models;
}

/**
 * Documents / roots to query for CD forms (scope + modal iframes).
 *
 * @param {ParentNode|Document} [scope=document]
 * @returns {ParentNode[]}
 */
export function getClickDimensionsSearchScopes(scope = document) {
	/** @type {ParentNode[]} */
	const scopes = [scope];

	scope.querySelectorAll?.('.regatta-cd-form__iframe').forEach((iframe) => {
		if (iframe instanceof HTMLIFrameElement && iframe.contentDocument) {
			scopes.push(iframe.contentDocument);
		}
	});

	return scopes;
}

/**
 * Every SurveyJS model for embedded CD forms in scope (inline, modal, iframe).
 *
 * @param {ParentNode|Document} [scope=document]
 * @returns {object[]}
 */
export function getAllClickDimensionsSurveyModels(scope = document) {
	/** @type {object[]} */
	const models = [];
	const seen = new WeakSet();

	for (const searchScope of getClickDimensionsSearchScopes(scope)) {
		for (const model of getSurveyModelsInScope(searchScope)) {
			if (!seen.has(model)) {
				seen.add(model);
				models.push(model);
			}
		}
	}

	return models;
}

/**
 * First SurveyJS model in scope (property prefill and legacy callers).
 *
 * @param {ParentNode|Document} [scope=document]
 * @returns {object|null}
 */
export function getClickDimensionsSurveyModel(scope = document) {
	const models = getAllClickDimensionsSurveyModels(scope);
	return models[0] ?? null;
}

/**
 * @param {Function} fn
 * @param {number} waitMs
 * @returns {(...args: unknown[]) => void}
 */
function debounce(fn, waitMs) {
	/** @type {ReturnType<typeof setTimeout>|undefined} */
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => fn(...args), waitMs);
	};
}

class ClickDimensionsHiddenFields {
	constructor() {
		this.scheduleConsentUpdate = debounce((scope) => {
			this.updateConsentFields(scope);
		}, 80);

		const domObserver = new MutationObserver(() => {
			if (document.querySelector('.sd-root-modern__wrapper, .sd-root-modern')) {
				this.scheduleConsentUpdate(document);
			}
		});

		domObserver.observe(document.body, { childList: true, subtree: true });

		document.addEventListener('regatta:clickdimensions-mount', (event) => {
			const container = event.detail?.container;
			const scope = isDomSearchScope(container) ? container : document;
			this.scheduleConsentUpdate(scope);
		});

		document.addEventListener('regatta:clickdimensions-iframe-ready', (event) => {
			const iframe = event.detail?.iframe;
			const scope =
				iframe instanceof HTMLIFrameElement && iframe.contentDocument
					? iframe.contentDocument
					: document;
			this.scheduleConsentUpdate(scope);
		});

		window.addEventListener('CookiebotOnAccept', () => {
			this.updateConsentFields(document);
		});
		window.addEventListener('CookiebotOnDecline', () => {
			this.updateConsentFields(document);
		});

		if (document.querySelector('.sd-root-modern__wrapper, .sd-root-modern')) {
			this.scheduleConsentUpdate(document);
		}
	}

	getCookiebotConsent() {
		const cb = window.Cookiebot;

		if (!cb || !cb.consent) {
			return { marketing: false, statistics: false };
		}

		return {
			marketing: cb.consent.marketing === true,
			statistics: cb.consent.statistics === true,
		};
	}

	/**
	 * @param {object} model SurveyJS model
	 * @param {boolean} marketing
	 * @param {boolean} statistics
	 */
	applyConsentToModel(model, marketing, statistics) {
		try {
			model.setValue(CONSENT_FIELD_MARKETING, marketing);
			model.setValue(CONSENT_FIELD_STATISTICS, statistics);
		} catch {
			/* field may be missing on this form definition */
		}
	}

	/**
	 * @param {ParentNode|Document} [scope=document]
	 */
	updateConsentFields(scope = document) {
		const models = getAllClickDimensionsSurveyModels(scope);
		if (!models.length) {
			return;
		}

		const { marketing, statistics } = this.getCookiebotConsent();

		for (const model of models) {
			this.applyConsentToModel(model, marketing, statistics);
		}
	}
}

new ClickDimensionsHiddenFields();
export default ClickDimensionsHiddenFields;
